import React, { useState } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdvancedSearchHelp: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'search' | 'preset'>('search');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 flex justify-between items-center sticky top-0">
          <h1 className="text-2xl font-bold text-white">📚 Guia de Pesquisa Avançada</h1>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 py-4 px-6 font-bold transition-colors ${
              activeTab === 'search'
                ? 'bg-blue-600 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            🔍 Como Pesquisar
          </button>
          <button
            onClick={() => setActiveTab('preset')}
            className={`flex-1 py-4 px-6 font-bold transition-colors ${
              activeTab === 'preset'
                ? 'bg-blue-600 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            💾 Salvar Presets
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-gray-300">
          {activeTab === 'search' ? (
            <>
              {/* Search by Text */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">1️⃣ Buscar por Texto</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>O que faz:</strong> Procura uma informação em múltiplos campos
                  </p>
                  <p className="text-sm">
                    <strong>Busca em:</strong> Número da linha, Nome do cliente, CPF/CNPJ, Contrato
                  </p>
                  <div className="bg-zinc-800 p-3 rounded mt-2 border-l-4 border-blue-500">
                    <p className="text-xs font-mono">🔎 Digite qualquer informação que lembre sobre a linha</p>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Exemplos:</strong>
                  </p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• <code className="bg-zinc-900 px-2 py-1 rounded text-green-400">(84) 98667-7652</code> - busca por número</li>
                    <li>• <code className="bg-zinc-900 px-2 py-1 rounded text-green-400">João Silva</code> - busca por nome</li>
                    <li>• <code className="bg-zinc-900 px-2 py-1 rounded text-green-400">123.456.789-00</code> - busca por CPF</li>
                    <li>• <code className="bg-zinc-900 px-2 py-1 rounded text-green-400">CT12345</code> - busca por contrato</li>
                  </ul>
                </div>
              </div>

              {/* Status Filter */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">2️⃣ Filtrar por Status</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-3">
                  <p className="text-sm">
                    <strong>O que faz:</strong> Mostra apenas as linhas com determinado status
                  </p>
                  <p className="text-sm">
                    <strong>Você pode selecionar múltiplos status simultaneamente!</strong>
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="bg-emerald-500 text-white px-3 py-1 rounded text-sm font-medium">✓ Adimplentes</div>
                      <span className="text-xs">Clientes que pagam em dia</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="bg-orange-500 text-white px-3 py-1 rounded text-sm font-medium">⚠ Inadimplentes</div>
                      <span className="text-xs">Clientes com atraso</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="bg-gray-600 text-white px-3 py-1 rounded text-sm font-medium">? Sem Usuário</div>
                      <span className="text-xs">Linhas sem cliente vinculado</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Valor Range */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">3️⃣ Filtrar por Valor Mensal</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>O que faz:</strong> Mostra apenas linhas dentro de um intervalo de preço
                  </p>
                  <p className="text-sm">
                    <strong>Como usar:</strong> Digite o valor mínimo, máximo ou ambos
                  </p>
                  <div className="bg-zinc-800 p-3 rounded mt-2 border-l-4 border-green-500 space-y-2">
                    <p className="text-xs font-mono">💰 Apenas Mínimo: Mostra linhas com valor ≥ mínimo</p>
                    <p className="text-xs font-mono">💰 Apenas Máximo: Mostra linhas com valor ≤ máximo</p>
                    <p className="text-xs font-mono">💰 Ambos: Mostra linhas entre os dois valores</p>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Exemplo:</strong> Mínimo: 50, Máximo: 150 → Mostra linhas de R$ 50 a R$ 150
                  </p>
                </div>
              </div>

              {/* Data Range */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">4️⃣ Filtrar por Período</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>O que faz:</strong> Mostra linhas vinculadas/bloqueadas em um período específico
                  </p>
                  <p className="text-sm">
                    <strong>Considdera:</strong> Data de vínculo OU data de bloqueio
                  </p>
                  <div className="bg-zinc-800 p-3 rounded mt-2 border-l-4 border-purple-500">
                    <p className="text-xs font-mono">📅 Selecione data inicial e/ou data final</p>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Exemplo:</strong> De 01/11/2024 a 30/11/2024 → Mostra linhas vinculadas em novembro
                  </p>
                </div>
              </div>

              {/* Reset */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">🔄 Limpar Filtros</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    Clique em <strong>"🔄 Limpar Filtros"</strong> para remover todos os filtros e pesquisas
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Isso mostrará todas as linhas novamente
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* What is Preset */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">O que é um Preset?</h2>
                <div className="bg-zinc-700 p-4 rounded-lg">
                  <p className="text-sm">
                    Um <strong>preset é uma pesquisa salva</strong>. Você configura os filtros e salva com um nome para reutilizar depois.
                  </p>
                  <p className="text-sm mt-3">
                    Imagine que você frequentemente procura por <em>"clientes inadimplentes no valor entre R$ 100 e R$ 500"</em>. 
                    Você pode salvar isso como um preset e carregar em um clique!
                  </p>
                </div>
              </div>

              {/* How to Save */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">💾 Como Salvar um Preset</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-3">
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Configure os filtros que deseja (busca, status, valor, período)</li>
                    <li>Clique no botão <strong>"💾 Salvar como Preset"</strong></li>
                    <li>Digite um <strong>nome descritivo</strong> para o preset</li>
                    <li>Clique em <strong>"Salvar"</strong></li>
                  </ol>
                  <div className="bg-zinc-800 p-3 rounded mt-3 border-l-4 border-blue-500">
                    <p className="text-xs font-bold text-blue-300">💡 Dica</p>
                    <p className="text-xs mt-1">Use nomes claros como: "VIP Clientes", "Inadimplentes Jan 2025", "Alto Valor"</p>
                  </div>
                </div>
              </div>

              {/* How to Load */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">⭐ Como Carregar um Preset</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-3">
                  <p className="text-sm">
                    Seus presets salvos aparecem na seção <strong>"⭐ Meus Presets"</strong>
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-sm mt-2">
                    <li>Clique no nome do preset que deseja usar</li>
                    <li>Os filtros serão carregados automaticamente</li>
                    <li>A lista será filtrada segundo os parâmetros salvos</li>
                  </ol>
                </div>
              </div>

              {/* How to Delete */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">🗑️ Como Deletar um Preset</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    Cada preset tem um botão <strong>"✕"</strong> no lado direito
                  </p>
                  <p className="text-sm mt-2">
                    Clique nele para remover o preset (não há volta!)
                  </p>
                </div>
              </div>

              {/* Examples */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">📋 Exemplos de Presets Úteis</h2>
                <div className="bg-zinc-700 p-4 rounded-lg space-y-2">
                  <div className="bg-zinc-800 p-2 rounded text-sm">
                    <strong>Nome:</strong> "Inadimplentes Urgente"<br/>
                    <strong>Filtro:</strong> Status: Inadimplente, Valor: Min 50
                  </div>
                  <div className="bg-zinc-800 p-2 rounded text-sm">
                    <strong>Nome:</strong> "Clientes Premium"<br/>
                    <strong>Filtro:</strong> Status: Adimplente, Valor: Min 200
                  </div>
                  <div className="bg-zinc-800 p-2 rounded text-sm">
                    <strong>Nome:</strong> "Sem Usuário - Novembro"<br/>
                    <strong>Filtro:</strong> Status: Sem Usuário, Data: 01/11 a 30/11
                  </div>
                </div>
              </div>

              {/* Storage Info */}
              <div className="space-y-3">
                <h2 className="text-xl font-bold text-white">💾 Armazenamento</h2>
                <div className="bg-yellow-900/30 p-4 rounded-lg border border-yellow-600/50 text-sm">
                  <p className="text-yellow-300">
                    ⚠️ Seus presets são salvos <strong>no seu navegador</strong>
                  </p>
                  <p className="text-sm mt-2">
                    Se você limpar cache/histórico do navegador, os presets serão removidos
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-zinc-700 p-4 border-t border-zinc-600 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded transition-colors"
          >
            Entendi! ✓
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchHelp;
